// src/pages/components/JmiCommissionPayModal.jsx
import React, { useState } from "react";
import { X, Landmark, Calendar, ShieldCheck, AlertCircle } from "lucide-react";
import { writeBatch, doc } from "firebase/firestore";
import { db } from "../../services/firebase";

export const getFridayCycle = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sun, 5 = Fri
  const diffToLastFriday = day >= 5 ? day - 5 : day + 2;
  
  const lastFriday = new Date(d);
  lastFriday.setDate(d.getDate() - diffToLastFriday);
  lastFriday.setHours(0, 0, 0, 0);

  const nextFriday = new Date(lastFriday);
  nextFriday.setDate(lastFriday.getDate() + 7);
  nextFriday.setHours(23, 59, 59, 999);

  return {
    lastFriday,
    nextFriday,
    cycleLabel: `${lastFriday.toLocaleDateString("en-IN", { day: "numeric", month: "short" })} – ${nextFriday.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`,
    dueFridayLabel: `Due by Friday, ${nextFriday.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`,
  };
};

const JmiCommissionPayModal = ({
  orders = [],
  rtgsSaleRate = 0,
  totalJmiFine = 0,
  totalJmiAmount = 0,
  onClose,
  onSuccess,
  operatorId = "",
}) => {
  const [utrNumber, setUtrNumber] = useState("");
  const [paymentMode, setPaymentMode] = useState("RTGS");
  const [notes, setNotes] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");

  const fridayCycle = getFridayCycle();

  // Calculations
  const rate = parseFloat(rtgsSaleRate) || 0;
  const fineGrams = parseFloat(totalJmiFine) || 0;
  const makingCharges = parseFloat(totalJmiAmount) || 0;
  const fineAmount = fineGrams * rate;
  const exactTotalPayable = fineAmount + makingCharges;

  const round2 = (val) => Number((Number(val) || 0).toFixed(2));
  const round3 = (val) => Number((Number(val) || 0).toFixed(3));

  const handleConfirmSettlement = async () => {
    if (!utrNumber.trim()) {
      setError("Please enter the UTR Number for this exact transaction.");
      return;
    }

    if (orders.length === 0) {
      setError("No orders selected for settlement.");
      return;
    }

    setError("");
    setIsProcessing(true);

    try {
      const timestamp = Date.now();
      const randomSuffix = Math.random().toString(36).substring(2, 6).toUpperCase();
      const settlementId = `JMI-COMM-${timestamp}-${randomSuffix}`;
      const now = new Date();

      const batch = writeBatch(db);

      // 1. Create entry in jmiSettlements collection
      const jmiSettlementRef = doc(db, "jmiSettlements", settlementId);
      const settlementPayload = {
        settlementId,
        type: "JMI_COMMISSION_SETTLEMENT",
        operatorId: operatorId || localStorage.getItem("operatorId") || "",
        utrNumber: utrNumber.trim(),
        paymentMode,
        notes: notes.trim(),
        ordersCount: orders.length,
        orderIds: orders.map((o) => o.docId || o.id),
        
        // Exact Financials
        totalJmiFineGrams: round3(fineGrams),
        rtgsSaleRate: round2(rate),
        jmiFineAmount: round2(fineAmount),
        jmiMakingCharges: round2(makingCharges),
        exactTotalPayable: round2(exactTotalPayable),
        
        // Cycle
        fridayCycle: {
          period: fridayCycle.cycleLabel,
          dueFriday: fridayCycle.dueFridayLabel,
          settledAt: now,
        },
        settledAt: now,
        createdAt: now,
        status: "COMPLETED",
      };

      batch.set(jmiSettlementRef, settlementPayload);

      // 2. Also register in general settlements collection for reporting
      const generalSettlementRef = doc(db, "settlements", settlementId);
      batch.set(generalSettlementRef, {
        ...settlementPayload,
        party: "JMI",
        totalAmount: round2(exactTotalPayable),
        totalFine: round3(fineGrams),
      });

      // 3. Update all orders in orderList
      orders.forEach((order) => {
        const docId = order.docId || order.id;
        if (!docId) return;
        const orderRef = doc(db, "orderList", docId);
        batch.update(orderRef, {
          orderStatus: "PaymentSettled",
          jmiSettled: true,
          jmiCommissionSettled: true,
          jmiSettlementId: settlementId,
          jmiUtrNumber: utrNumber.trim(),
          jmiSettledAt: now,
          jmiRtgsRateUsed: round2(rate),
          jmiSettlementData: {
            settlementId,
            utrNumber: utrNumber.trim(),
            settledAt: now,
            exactAmount: round2(exactTotalPayable),
          },
        });
      });

      await batch.commit();

      if (onSuccess) {
        onSuccess(
          `✅ JMI Commission cleared successfully! Settlement ID: ${settlementId} · UTR: ${utrNumber.trim()}`
        );
      }
      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error("[JmiCommissionPayModal] Settlement error:", err);
      setError(`Settlement failed: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="jmi-pm-overlay" onClick={onClose}>
      <div className="jmi-pm-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="jmi-pm-header">
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div className="jmi-pm-icon-wrap">
              <Landmark size={20} color="#6366f1" />
            </div>
            <div>
              <h3 className="jmi-pm-title">Settle JMI Commission</h3>
              <p className="jmi-pm-subtitle">Friday-to-Friday Commission Clearance</p>
            </div>
          </div>
          <button className="jmi-pm-close" onClick={onClose} disabled={isProcessing}>
            <X size={20} />
          </button>
        </div>

        {/* Cycle Banner */}
        <div className="jmi-pm-cycle-banner">
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Calendar size={16} color="#4338ca" />
            <span style={{ fontWeight: 600, color: "#3730a3", fontSize: "13px" }}>
              Settlement Cycle: {fridayCycle.cycleLabel}
            </span>
          </div>
          <span className="jmi-pm-cycle-badge">{fridayCycle.dueFridayLabel}</span>
        </div>

        <div className="jmi-pm-body">
          {/* Summary Breakdown Card */}
          <div className="jmi-pm-summary-card">
            <div className="jmi-pm-grid-2">
              <div className="jmi-pm-stat">
                <span className="jmi-pm-stat-label">Total Orders</span>
                <span className="jmi-pm-stat-val">{orders.length}</span>
              </div>
              <div className="jmi-pm-stat">
                <span className="jmi-pm-stat-label">RTGS Sale Rate</span>
                <span className="jmi-pm-stat-val" style={{ color: "#059669" }}>
                  ₹{rate.toLocaleString("en-IN", { minimumFractionDigits: 2 })}/g
                </span>
              </div>
              <div className="jmi-pm-stat">
                <span className="jmi-pm-stat-label">Total JMI Fine (g)</span>
                <span className="jmi-pm-stat-val">{fineGrams.toFixed(3)}g</span>
              </div>
              <div className="jmi-pm-stat">
                <span className="jmi-pm-stat-label">Fine Value (Fine × Rate)</span>
                <span className="jmi-pm-stat-val">
                  ₹{fineAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="jmi-pm-stat">
                <span className="jmi-pm-stat-label">JMI Making Charges</span>
                <span className="jmi-pm-stat-val">
                  ₹{makingCharges.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="jmi-pm-stat jmi-pm-stat--highlight">
                <span className="jmi-pm-stat-label" style={{ color: "#3730a3", fontWeight: 700 }}>
                  Exact Total Payable
                </span>
                <span className="jmi-pm-stat-val" style={{ color: "#4338ca", fontSize: "20px" }}>
                  ₹{exactTotalPayable.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>

          {/* Form Fields */}
          <div className="jmi-pm-form-group">
            <label className="jmi-pm-label">
              Bank Transaction UTR Number <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              type="text"
              className="jmi-pm-input"
              placeholder="e.g. UTR123456789012"
              value={utrNumber}
              onChange={(e) => setUtrNumber(e.target.value)}
              disabled={isProcessing}
              autoFocus
            />
            <span className="jmi-pm-hint">
              Enter the exact transaction UTR number transferred to JMI for ₹
              {exactTotalPayable.toFixed(2)}.
            </span>
          </div>

          <div className="jmi-pm-grid-2">
            <div className="jmi-pm-form-group">
              <label className="jmi-pm-label">Payment Mode</label>
              <select
                className="jmi-pm-input"
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
                disabled={isProcessing}
              >
                <option value="RTGS">RTGS</option>
                <option value="NEFT">NEFT</option>
                <option value="IMPS">IMPS</option>
                <option value="Bank Transfer">Bank Transfer</option>
              </select>
            </div>
            <div className="jmi-pm-form-group">
              <label className="jmi-pm-label">Remarks / Note (Optional)</label>
              <input
                type="text"
                className="jmi-pm-input"
                placeholder="e.g. Friday clearance batch"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isProcessing}
              />
            </div>
          </div>

          {/* Orders Preview List */}
          <div className="jmi-pm-orders-preview">
            <div className="jmi-pm-preview-title">
              Orders Included in this Clearance ({orders.length})
            </div>
            <div className="jmi-pm-preview-list">
              {orders.slice(0, 10).map((o, idx) => (
                <div key={o.id || o.docId || idx} className="jmi-pm-preview-item">
                  <span><strong>#{o.id || o.docId}</strong> ({o.productName || "Product"})</span>
                  <span>Seller: {o.sellerId || "—"}</span>
                  <span>Fine: {(parseFloat(o.jmiFine) || 0).toFixed(3)}g</span>
                  <span>MC: ₹{(parseFloat(o.totalAmountJmi) || 0).toFixed(2)}</span>
                </div>
              ))}
              {orders.length > 10 && (
                <div style={{ textAlign: "center", color: "#6b7280", fontSize: "12px", paddingTop: "4px" }}>
                  + {orders.length - 10} more orders
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="jmi-pm-alert jmi-pm-alert--error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="jmi-pm-footer">
          <button
            type="button"
            className="jmi-pm-btn-cancel"
            onClick={onClose}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button
            type="button"
            className="jmi-pm-btn-confirm"
            onClick={handleConfirmSettlement}
            disabled={isProcessing || !utrNumber.trim()}
          >
            {isProcessing ? (
              "Processing Clearance…"
            ) : (
              <>
                <ShieldCheck size={16} /> Confirm & Clear JMI Settlement (₹{exactTotalPayable.toFixed(2)})
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        .jmi-pm-overlay {
          position: fixed; inset: 0;
          background: rgba(15, 23, 42, 0.65);
          backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          z-index: 1100; padding: 16px;
        }
        .jmi-pm-modal {
          background: #ffffff; border-radius: 14px;
          width: 100%; max-width: 680px; max-height: 90vh;
          display: flex; flex-direction: column;
          box-shadow: 0 25px 60px -15px rgba(0, 0, 0, 0.3);
          overflow: hidden; border: 1px solid #e2e8f0;
        }
        .jmi-pm-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 20px; border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
        }
        .jmi-pm-icon-wrap {
          width: 36px; height: 36px; border-radius: 8px;
          background: #eef2ff; display: flex; align-items: center; justify-content: center;
        }
        .jmi-pm-title { margin: 0; font-size: 17px; font-weight: 700; color: #1e293b; }
        .jmi-pm-subtitle { margin: 2px 0 0; font-size: 12px; color: #64748b; }
        .jmi-pm-close {
          background: none; border: none; cursor: pointer; color: #94a3b8;
          padding: 6px; border-radius: 6px; display: flex; align-items: center;
        }
        .jmi-pm-close:hover { background: #f1f5f9; color: #1e293b; }
        .jmi-pm-cycle-banner {
          display: flex; align-items: center; justify-content: space-between;
          padding: 10px 20px; background: #eef2ff; border-bottom: 1px solid #c7d2fe;
          flex-wrap: wrap; gap: 8px;
        }
        .jmi-pm-cycle-badge {
          background: #4338ca; color: #ffffff; font-size: 11px;
          font-weight: 600; padding: 2px 8px; border-radius: 12px;
        }
        .jmi-pm-body {
          padding: 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 16px;
        }
        .jmi-pm-summary-card {
          background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px;
        }
        .jmi-pm-grid-2 {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px;
        }
        .jmi-pm-stat {
          display: flex; flex-direction: column; gap: 2px;
        }
        .jmi-pm-stat-label { font-size: 12px; color: #64748b; font-weight: 500; }
        .jmi-pm-stat-val { font-size: 15px; color: #1e293b; font-weight: 700; }
        .jmi-pm-stat--highlight {
          background: #ede9fe; padding: 8px 12px; border-radius: 8px; border: 1px solid #c4b5fd;
        }
        .jmi-pm-form-group { display: flex; flex-direction: column; gap: 6px; }
        .jmi-pm-label { font-size: 13px; font-weight: 600; color: #334155; }
        .jmi-pm-input {
          padding: 10px 12px; border: 1.5px solid #cbd5e1; border-radius: 8px;
          font-size: 14px; color: #1e293b; outline: none; transition: border-color 0.15s;
        }
        .jmi-pm-input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15); }
        .jmi-pm-hint { font-size: 12px; color: #64748b; }
        .jmi-pm-orders-preview {
          border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px; background: #fafafa;
        }
        .jmi-pm-preview-title { font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 6px; }
        .jmi-pm-preview-list { max-height: 120px; overflow-y: auto; display: flex; flex-direction: column; gap: 4px; }
        .jmi-pm-preview-item {
          display: flex; justify-content: space-between; font-size: 11px; color: #334155;
          padding: 4px 6px; background: #ffffff; border-radius: 4px; border: 1px solid #f1f5f9;
        }
        .jmi-pm-alert {
          padding: 10px 14px; border-radius: 8px; font-size: 13px; display: flex; align-items: center; gap: 8px;
        }
        .jmi-pm-alert--error { background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; }
        .jmi-pm-footer {
          display: flex; justify-content: flex-end; gap: 10px; padding: 14px 20px;
          border-top: 1px solid #e2e8f0; background: #f8fafc;
        }
        .jmi-pm-btn-cancel {
          padding: 9px 16px; border: 1px solid #cbd5e1; border-radius: 8px;
          background: #ffffff; color: #475569; font-weight: 600; font-size: 13px; cursor: pointer;
        }
        .jmi-pm-btn-cancel:hover { background: #f1f5f9; }
        .jmi-pm-btn-confirm {
          padding: 9px 20px; border: none; border-radius: 8px;
          background: #4f46e5; color: #ffffff; font-weight: 600; font-size: 13px;
          cursor: pointer; display: flex; align-items: center; gap: 6px;
          transition: background-color 0.15s;
        }
        .jmi-pm-btn-confirm:hover:not(:disabled) { background: #4338ca; }
        .jmi-pm-btn-confirm:disabled { opacity: 0.5; cursor: not-allowed; }
      `}</style>
    </div>
  );
};

export default JmiCommissionPayModal;
